import { useEffect, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { FaCreditCard, FaCheckCircle } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import PaymentService from "../rest/paymentsService";
import { formatCurrency } from "../utils/CurrencyUtils";

const BRAND_COLORS = {
    VISA: "bg-blue-700",
    MASTERCARD: "bg-red-600",
    ELO: "bg-yellow-500",
    AMEX: "bg-teal-600",
};

const SavedCardItem = ({ card, selected, onSelect }) => {
    const brand = (card.cardBrand || "").toUpperCase();
    const color = BRAND_COLORS[brand] || "bg-gray-600";
    return (
        <div
            onClick={() => onSelect(card.creditCardToken)}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                ${selected ? "border-custom_blue bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
        >
            <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center text-white flex-shrink-0`}>
                <FaCreditCard size={20} />
            </div>
            <div className="flex-1">
                <p className="font-bold text-slate-800">{card.cardBrand} •••• {card.cardLastFour}</p>
                {card.isDefault && <p className="text-xs text-custom_blue font-semibold">Padrão</p>}
            </div>
            {selected && <FaCheckCircle className="text-custom_blue" size={22} />}
        </div>
    );
};

const NewCardForm = ({ form, onChange }) => {
    const field = (label, key, placeholder, opts = {}) => (
        <div className={opts.half ? "" : "col-span-2"}>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
            <input
                type={opts.type || "text"}
                inputMode={opts.inputMode}
                maxLength={opts.maxLength}
                value={form[key] || ""}
                onChange={e => onChange(key, e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 outline-none focus:border-custom_blue text-sm"
            />
        </div>
    );

    return (
        <div className="grid grid-cols-2 gap-3">
            {field("Número do Cartão", "number", "0000 0000 0000 0000", { inputMode: "numeric", maxLength: 19 })}
            {field("Mês de Vencimento", "expiryMonth", "MM", { half: true, inputMode: "numeric", maxLength: 2 })}
            {field("Ano de Vencimento", "expiryYear", "AAAA", { half: true, inputMode: "numeric", maxLength: 4 })}
            {field("CVV", "cvv", "123", { half: true, inputMode: "numeric", maxLength: 4 })}
            {field("Nome no Cartão", "holderName", "Como impresso no cartão")}
            {field("CPF/CNPJ do Titular", "holderCpfCnpj", "000.000.000-00", { half: true, inputMode: "numeric" })}
            {field("E-mail do Titular", "holderEmail", "email@exemplo.com", { half: true })}
            {field("CEP", "holderPostalCode", "00000-000", { half: true, inputMode: "numeric", maxLength: 9 })}
            {field("Número do Endereço", "holderAddressNumber", "123", { half: true, inputMode: "numeric" })}
            {field("Bairro", "holderProvince", "Bairro", { half: true })}
            {field("Telefone (opcional)", "holderPhone", "(00) 00000-0000", { half: true })}
        </div>
    );
};

const CreditCardPaymentModal = ({ customer, clientCode, totalAmount, onPay, onCancel }) => {
    const [savedCards, setSavedCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(true);
    const [view, setView] = useState("cards");
    const [selectedToken, setSelectedToken] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [cardForm, setCardForm] = useState({
        holderName: customer?.name || "",
        number: "", expiryMonth: "", expiryYear: "", cvv: "",
        holderCpfCnpj: "", holderEmail: "", holderPhone: "",
        holderPostalCode: "", holderAddressNumber: "", holderProvince: "",
    });

    useEffect(() => {
        if (!customer?.login) { setLoadingCards(false); setView("newCard"); return; }
        PaymentService.getSavedCards(customer.login, clientCode)
            .then(cards => {
                setSavedCards(cards || []);
                if (!cards || cards.length === 0) setView("newCard");
                else {
                    const def = cards.find(c => c.isDefault) || cards[0];
                    setSelectedToken(def.creditCardToken);
                }
            })
            .catch(() => setView("newCard"))
            .finally(() => setLoadingCards(false));
    }, [customer?.login]);

    const handleFieldChange = (key, value) => setCardForm(prev => ({ ...prev, [key]: value }));

    const isNewCardValid = () => {
        const required = ["number", "expiryMonth", "expiryYear", "cvv",
            "holderName", "holderCpfCnpj", "holderEmail",
            "holderPostalCode", "holderAddressNumber", "holderProvince"];
        return required.every(k => cardForm[k]?.trim().length > 0);
    };

    const handlePay = async () => {
        setSubmitting(true);
        if (view === "cards") {
            onPay({ creditCardToken: selectedToken });
        } else {
            onPay({ creditCardData: cardForm });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] animate-in zoom-in duration-300">
                <div className="bg-custom_blue p-5 text-white text-center rounded-t-2xl flex-shrink-0">
                    <h2 className="text-xl font-bold">Pagamento com Cartão</h2>
                    <p className="opacity-90 text-sm mt-0.5">Total: {formatCurrency(totalAmount)}</p>
                </div>

                {loadingCards ? (
                    <div className="flex-1 flex items-center justify-center p-10">
                        <AiOutlineLoading className="animate-spin text-custom_blue text-4xl" />
                    </div>
                ) : (
                    <>
                        {savedCards.length > 0 && (
                            <div className="flex border-b border-slate-200 flex-shrink-0">
                                <button
                                    onClick={() => setView("cards")}
                                    className={`flex-1 py-3 text-sm font-bold transition-colors
                                        ${view === "cards" ? "text-custom_blue border-b-2 border-custom_blue" : "text-slate-500"}`}
                                >
                                    Cartões Salvos
                                </button>
                                <button
                                    onClick={() => setView("newCard")}
                                    className={`flex-1 py-3 text-sm font-bold transition-colors
                                        ${view === "newCard" ? "text-custom_blue border-b-2 border-custom_blue" : "text-slate-500"}`}
                                >
                                    Novo Cartão
                                </button>
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto p-5">
                            {view === "cards" ? (
                                <div className="space-y-3">
                                    {savedCards.map(card => (
                                        <SavedCardItem
                                            key={card.id}
                                            card={card}
                                            selected={selectedToken === card.creditCardToken}
                                            onSelect={setSelectedToken}
                                        />
                                    ))}
                                    <button
                                        onClick={() => setView("newCard")}
                                        className="w-full py-3 border-2 border-dashed border-slate-300 text-slate-500 font-bold rounded-xl flex items-center justify-center gap-2 hover:border-custom_blue hover:text-custom_blue transition-colors"
                                    >
                                        <MdAdd size={20} /> Usar novo cartão
                                    </button>
                                </div>
                            ) : (
                                <NewCardForm form={cardForm} onChange={handleFieldChange} />
                            )}
                        </div>

                        <div className="p-5 border-t border-slate-100 flex gap-3 flex-shrink-0">
                            <button
                                onClick={onCancel}
                                disabled={submitting}
                                className="flex-1 py-3 border-2 border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handlePay}
                                disabled={submitting || (view === "cards" && !selectedToken) || (view === "newCard" && !isNewCardValid())}
                                className="flex-1 py-3 bg-custom_blue text-white font-bold rounded-xl shadow-md flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                            >
                                {submitting
                                    ? <AiOutlineLoading className="animate-spin text-xl" />
                                    : <><FaCreditCard /> Cobrar {formatCurrency(totalAmount)}</>
                                }
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreditCardPaymentModal;
